import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post } from "~/Services";
import { passengerHost } from "~/Utils";
import { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn, renderImageColumn } from "~/Utils/TableRenderFunction";
import { useHelpContentDocumentDetails } from "./Flightinfo.hooks";

export const PageContent = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  const publishedData: TypeData[] = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
  ];

  const { data: drilldown, refetch: refetchDocument } = useHelpContentDocumentDetails(id || "");

  // Refetch detail on row change
  useEffect(() => {
    if (id) refetchDocument();
  }, [id]);

  // Reset filters on tab change
  useEffect(() => {
    if (query.get("subActiveTab") === "parentAirport") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("subActiveTab")]);

  // ✅ Filter and search logic
  useEffect(() => {
    let filteredData = [...(Array.isArray(data) ? data : [])];

    // Search by airport or level
    if (searchValue) {
      const searchLower = decodeHTML(searchValue || "").toLowerCase();
      filteredData = filteredData.filter((item: any) => {
        const airport = decodeHTML(item.field_location_airports || "").toLowerCase();
        const level = decodeHTML(item.english_title || "").toLowerCase();
        return airport.includes(searchLower) || level.includes(searchLower);
      });
    }

    // Published filter
    if (selectedPublished !== "") {
      filteredData = filteredData.filter(
        (item) => String(item.published || "").toLowerCase() === String(selectedPublished || "").toLowerCase()
      );
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  // Fetch records
  const getRecords = () => {
    setIsLoading(true);
    passengerCMSApi
      .get(`/api/flight-information-card?airport=${currentAirport}`)
      .then((res: any) => {
        const envelopes = res?.data || [];
        const normalizedData = envelopes.map((item: any) => ({
          ...item,
          published: String(item.published || "false").toLowerCase(),
        }));
        setData(normalizedData);
        setTotalRecords(normalizedData.length);
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getRecords();
  }, []);
  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    const order = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(order);
    setOrderByName(column.dataIndex);
    setSortedData([...sortedData].sort((a, b) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (!apiUrl) return;

    const payload = {
      action,
      method,
      module: "Flight Information",
      subModule: "pageContents",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: data,
      status,
    };

    post("/management/publish", payload)
      .then(() => {
        openToast({ content: "Data Sent for Review", type: "success" });
        navigate("/dedicated/status-information?activeTab=pending");
      })
      .catch(() => openToast({ content: "Failed to send data", type: "error" }));
  };

  async function sendDeleteBrandRequest(reviewNotes: string) {
    await onSubmit(
      selectedRowData,
      `${passengerHost}/api/get-help-card-crud-api-data/${selectedRowData?.id}`,
      "DELETE",
      "DELETE",
      "PENDING",
      reviewNotes
    );
  }

  const columns: TableColumnsType = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 100,
      render: renderImageColumn,
    },
    {
      title: "Card Name",
      dataIndex: "english_title",
      key: "english_title",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder && orderByName === "english_title" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    // {
    //   title: "Redirect URL",
    //   dataIndex: "redirect_url_english",
    //   key: "redirect_url_english",
    //   render: (text) => decodeHTML(text),
    // },

    // {
    //   title: "Published",
    //   dataIndex: "published",
    //   key: "published",
    //   render: renderPublishedColumn,
    //   sorter: true,
    //   sortOrder: sortOrder && orderByName === "published" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    // },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder: sortOrder && orderByName === "created" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        const created = record?.created ? moment(record.created).format("YYYY-MM-DD HH:mm:ss") : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;
        if (created && updated && created === updated) return "-";
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder: sortOrder && orderByName === "changed" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/passenger/flight-information/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible
        ),
    },
  ];

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        isLoading={isLoading}
        exportFileName="OUTLET"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search"}
        onClick={(record) => setSelectedRowData(record)}
        // additionalCTA={
        //   <div className="flex items-center space-x-2">
        //     <ActionDropdown
        //       value={selectedPublished}
        //       options={publishedData}
        //       onChange={handlePublishedChange}
        //       placeholder="Select Published"
        //       className="w-48"
        //     />
        //   </div>
        // }
      />

      {/* Right aligned pagination */}
      <div className="pagination flex justify-end mt-4">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          // onChange={(curPage, newPageSize) => {
          //   setCurrentPage(curPage - 1);
          //   if (newPageSize !== pageSize) {
          //     setPageSize(newPageSize);
          //     setCurrentPage(0);
          //   }
          // }}
          // showSizeChanger
          // pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "pageContents") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteBrandRequest}
        defaultReviewNotes="Kindly review this Flight Information"
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  const valueA = (a[sortBy] ?? "").toString().toLowerCase();
  const valueB = (b[sortBy] ?? "").toString().toLowerCase();
  if (order === "desc") return valueA < valueB ? 1 : -1;
  if (order === "asc") return valueA > valueB ? 1 : -1;
  return 0;
}
